import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

const TYPE_LABEL = {
  support: "Поддержка",
  payment: "Оплата",
  registration: "Регистрация",
};

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M18 16v-5a6 6 0 10-12 0v5l-2 2h16l-2-2z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M10 19a2 2 0 004 0"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function PlatformNotificationsBell({ unread = 0, onOpenChange }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await platformAPI.listNotifications({ limit: 20 });
      setItems(data.notifications || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    load();
    onOpenChange?.(true);
    const onDoc = (e) => {
      if (!panelRef.current?.contains(e.target)) {
        setOpen(false);
        onOpenChange?.(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, load, onOpenChange]);

  const openItem = async (item) => {
    try {
      if (!item.read_at) {
        await platformAPI.markNotificationRead(item.id);
        window.dispatchEvent(new CustomEvent("baysport:platform-notify"));
      }
    } catch {
      /* ignore */
    }
    setOpen(false);
    onOpenChange?.(false);
    if (item.link) navigate(item.link);
  };

  const markAll = async () => {
    try {
      await platformAPI.markAllNotificationsRead();
      window.dispatchEvent(new CustomEvent("baysport:platform-notify"));
      load();
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={s.notifyWrap} ref={panelRef}>
      <button
        type="button"
        className={s.notifyBell}
        aria-label="Уведомления"
        aria-expanded={open}
        onClick={() => {
          if (window.matchMedia("(max-width: 900px)").matches) {
            navigate("/admin/notifications");
            return;
          }
          const next = !open;
          setOpen(next);
          if (!next) onOpenChange?.(false);
        }}
      >
        <BellIcon />
        {unread > 0 ? (
          <span className={s.notifyBadge}>{unread > 99 ? "99+" : unread}</span>
        ) : null}
      </button>

      {open ? (
        <div className={s.notifyPanel} role="dialog" aria-label="Уведомления">
          <div className={s.notifyPanelHead}>
            <strong>Уведомления</strong>
            <button type="button" className={s.notifyMarkAll} onClick={markAll}>
              Прочитать все
            </button>
          </div>
          {loading ? (
            <p className={s.notifyEmpty}>Загрузка…</p>
          ) : items.length === 0 ? (
            <p className={s.notifyEmpty}>Пока тихо</p>
          ) : (
            <ul className={s.notifyList}>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`${s.notifyItem} ${item.read_at ? "" : s.notifyItemUnread}`}
                    onClick={() => openItem(item)}
                  >
                    <span className={s.notifyType}>
                      {TYPE_LABEL[item.type] || item.type}
                    </span>
                    <strong className={s.notifyTitle}>{item.title}</strong>
                    {item.body ? (
                      <span className={s.notifyBody}>{item.body}</span>
                    ) : null}
                    <span className={s.notifyTime}>
                      {formatWhen(item.created_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/admin/notifications"
            className={s.notifyFooterLink}
            onClick={() => {
              setOpen(false);
              onOpenChange?.(false);
            }}
          >
            Все уведомления
          </Link>
        </div>
      ) : null}
    </div>
  );
}

export default function PlatformNotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await platformAPI.listNotifications({ limit: 100 });
      setItems(data.notifications || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openItem = async (item) => {
    try {
      if (!item.read_at) {
        await platformAPI.markNotificationRead(item.id);
        window.dispatchEvent(new CustomEvent("baysport:platform-notify"));
      }
    } catch {
      /* ignore */
    }
    if (item.link) navigate(item.link);
  };

  return (
    <div className={s.page}>
      <PlatformPageHeader
        backTo="/admin/more"
        backLabel="Ещё"
        eyebrow="Алерты"
        title="Уведомления"
        lead="Новые клубы, оплаты тарифов и сообщения в поддержку."
        actions={
          <button
            type="button"
            className={s.heroActionBtnGhost}
            onClick={async () => {
              await platformAPI.markAllNotificationsRead();
              window.dispatchEvent(new CustomEvent("baysport:platform-notify"));
              load();
            }}
          >
            Прочитать все
          </button>
        }
      />

      {loading ? <div className={s.loading}>Загрузка…</div> : null}

      {!loading && !items.length ? (
        <div className={s.supportEmpty}>
          <p className={s.supportEmptyTitle}>Нет уведомлений</p>
          <p className={s.supportEmptyHint}>
            Здесь появятся регистрации, оплаты и обращения в поддержку.
          </p>
        </div>
      ) : null}

      {!loading && items.length ? (
        <ul className={s.notifyPageList}>
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className={`${s.notifyPageItem} ${item.read_at ? "" : s.notifyItemUnread}`}
                onClick={() => openItem(item)}
              >
                <span className={s.notifyType}>
                  {TYPE_LABEL[item.type] || item.type}
                </span>
                <strong>{item.title}</strong>
                {item.body ? <span>{item.body}</span> : null}
                <small>{formatWhen(item.created_at)}</small>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
